/**
 * ProductScanner — Auto-recognition scanner for Wellness Anar
 */
class ProductScanner {
  constructor(options = {}) {
    this.workerUrl = options.workerUrl || '';
    this.modal = null;
    this.videoStream = null;
    this.fileInput = null;
    this.state = 'idle';
    this.scanning = false;
  }

  init() {
    this.modal = document.getElementById('scannerModal');
    if (!this.modal) return;

    this.fileInput = this.modal.querySelector('.scanner-file-input');
    this.videoEl = this.modal.querySelector('.scanner-camera video');
    this.statusEl = this.modal.querySelector('.scanner-camera__status');
    this.progressFill = this.modal.querySelector('.scanner-camera__progress-fill');
    this.progressText = this.modal.querySelector('.scanner-camera__progress-text');
    this.cameraEl = this.modal.querySelector('.scanner-camera');

    // Bind ALL triggers — redirect to Nutrilens camera
    document.querySelectorAll('[data-scanner-open]').forEach((btn) => {
      btn.addEventListener('click', (e) => { e.preventDefault(); window.location.href = '/pages/nutrilens#camera'; });
    });

    // Close buttons
    this.modal.querySelectorAll('.scanner-modal__close').forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) this.close();
    });

    // Gallery buttons — bind ALL of them
    this.modal.querySelectorAll('[data-scanner-gallery]').forEach((btn) => {
      btn.addEventListener('click', (e) => { e.preventDefault(); this.openGallery(); });
    });

    // Retry buttons — bind ALL of them
    this.modal.querySelectorAll('[data-scanner-retry]').forEach((btn) => {
      btn.addEventListener('click', (e) => { e.preventDefault(); this.retry(); });
    });

    // File input
    this.fileInput?.addEventListener('change', (e) => {
      const file = e.target.files[0];
      if (file) this.processFile(file);
    });

    // Keyboard
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('is-open')) {
        this.close();
      }
    });
  }

  open() {
    this.modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.setState('idle');
    this.startCamera();
  }

  close() {
    this.modal.classList.remove('is-open');
    document.body.style.overflow = '';
    this.stopCamera();
    this.setState('idle');
  }

  setState(state) {
    this.state = state;
    const body = this.modal.querySelector('.scanner-modal__body');
    body.querySelectorAll('[data-state]').forEach((el) => {
      el.classList.toggle('is-active', el.dataset.state === state);
    });
  }

  setStatus(text) {
    if (this.statusEl) this.statusEl.textContent = text;
  }

  setProgress(pct) {
    const val = Math.min(Math.round(pct), 100);
    if (this.progressFill) this.progressFill.style.width = `${val}%`;
    if (this.progressText) this.progressText.textContent = `${val}%`;
  }

  async startCamera() {
    try {
      const constraints = {
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      this.videoStream = stream;
      this.videoEl.srcObject = stream;
      this.videoEl.play();

      const track = stream.getVideoTracks()[0];
      this.videoTrack = track;

      if (track?.applyConstraints) {
        try {
          const caps = track.getCapabilities?.() || {};
          const advanced = {};
          if (caps.focusMode?.includes('continuous')) advanced.focusMode = 'continuous';
          if (caps.exposureMode?.includes('continuous')) advanced.exposureMode = 'continuous';
          // Start at minimum zoom (1x wide angle)
          if (caps.zoom) {
            advanced.zoom = caps.zoom.min;
            this.zoomMin = caps.zoom.min;
            this.zoomMax = caps.zoom.max;
            this.currentZoom = caps.zoom.min;
          }
          if (Object.keys(advanced).length > 0) {
            await track.applyConstraints({ advanced: [advanced] });
          }
        } catch { /* OK */ }
      }

      this.initZoomControls();
      this.cameraEl?.classList.add('is-active');
      this.setState('camera');
      this.setStatus('제품 라벨을 비추고 셔터를 눌러주세요');
      this.setProgress(0);
      this.bindShutter();
    } catch {
      this.setState('idle');
    }
  }

  initZoomControls() {
    const zoomBar = this.modal.querySelector('.scanner-camera__zoom');
    if (!zoomBar) return;

    if (!this.zoomMin || !this.zoomMax || this.zoomMin === this.zoomMax) {
      zoomBar.style.display = 'none';
      return;
    }

    zoomBar.style.display = 'flex';
    const buttons = zoomBar.querySelectorAll('[data-zoom]');
    buttons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const level = parseFloat(btn.dataset.zoom);
        this.setZoom(level);
        buttons.forEach((b) => b.classList.remove('is-active'));
        btn.classList.add('is-active');
      });
    });
    // Activate 1x by default
    const btn1x = zoomBar.querySelector('[data-zoom="1"]');
    if (btn1x) btn1x.classList.add('is-active');
  }

  async setZoom(multiplier) {
    if (!this.videoTrack || !this.zoomMin) return;
    const zoom = Math.min(Math.max(this.zoomMin * multiplier, this.zoomMin), this.zoomMax);
    this.currentZoom = zoom;
    try {
      await this.videoTrack.applyConstraints({ advanced: [{ zoom }] });
    } catch { /* OK */ }
  }

  stopCamera() {
    if (this.videoStream) {
      this.videoStream.getTracks().forEach((t) => t.stop());
      this.videoStream = null;
    }
    this.cameraEl?.classList.remove('is-active');
    this.cameraEl?.classList.remove('is-scanning');
  }

  bindShutter() {
    const shutterBtn = this.modal.querySelector('[data-scanner-shutter]');
    if (shutterBtn && !shutterBtn._bound) {
      shutterBtn._bound = true;
      shutterBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.captureAndScan();
      });
    }
  }

  async captureAndScan() {
    if (!this.videoStream || this.state !== 'camera' || this.scanning) return;
    this.scanning = true;

    // Capture frame
    const canvas = document.createElement('canvas');
    const video = this.videoEl;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const dataUrl = this.resizeImage(canvas, 768);

    // Send to Nutrilens for unified food/product analysis
    this.sendToNutrilens(dataUrl);
  }

  freezeCamera(canvas) {
    // Hide live video, show static captured image
    if (this.videoEl) this.videoEl.style.display = 'none';
    this.unfreezeCamera(); // remove any existing preview
    const preview = document.createElement('img');
    preview.className = 'scanner-camera__preview';
    preview.src = canvas.toDataURL('image/jpeg', 0.9);
    this.cameraEl?.insertBefore(preview, this.cameraEl.firstChild);
  }

  unfreezeCamera() {
    const prev = this.cameraEl?.querySelector('.scanner-camera__preview');
    if (prev) prev.remove();
    if (this.videoEl) this.videoEl.style.display = '';
  }

  openGallery() {
    this.fileInput?.click();
  }

  processFile(file) {
    if (file.size > 10 * 1024 * 1024) {
      this.showError('이미지가 너무 큽니다. 10MB 이하로 선택해주세요.');
      return;
    }
    this.stopCamera();
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        canvas.getContext('2d').drawImage(img, 0, 0);
        const dataUrl = this.resizeImage(canvas, 768);
        this.sendToNutrilens(dataUrl);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  }

  sendToNutrilens(imageDataUrl) {
    this.stopCamera();
    this.scanning = false;
    try {
      sessionStorage.setItem('ss_pending_image', imageDataUrl);
    } catch(e) {}
    this.close();
    window.location.href = '/pages/nutrilens#auto-scan';
  }

  resizeImage(canvas, maxSize) {
    let { width, height } = canvas;
    if (width > maxSize || height > maxSize) {
      const ratio = Math.min(maxSize / width, maxSize / height);
      const resized = document.createElement('canvas');
      resized.width = Math.round(width * ratio);
      resized.height = Math.round(height * ratio);
      resized.getContext('2d').drawImage(canvas, 0, 0, resized.width, resized.height);
      return resized.toDataURL('image/jpeg', 0.85);
    }
    return canvas.toDataURL('image/jpeg', 0.85);
  }

  async analyze(imageDataUrl) {
    this.setState('loading');

    try {
      const response = await fetch(`${this.workerUrl}/api/scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image: imageDataUrl }),
      });

      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      const data = await response.json();
      this.showResult(data);
      this.saveToHistory(data);
    } catch (err) {
      console.error('Scan failed:', err);
      this.showError('분석에 실패했습니다. 다시 시도해주세요.');
    }
  }

  showResult(data) {
    this.setState('result');
    const container = this.modal.querySelector('.scanner-result');

    // Match confidence badge
    const badge = container.querySelector('.scanner-result__badge');
    if (data.matched && data.confidence) {
      const pct = Math.round((data.confidence || 0) * 100);
      badge.querySelector('.scanner-result__badge-text').textContent = `${pct}% 일치`;
      badge.style.display = 'flex';
    } else {
      badge.style.display = 'none';
    }

    if (data.matched && data.product) {
      const card = container.querySelector('.scanner-result__card');
      const productUrl = data.product.url;
      card.href = productUrl;
      card.querySelector('.scanner-result__image').src = data.product.image || '';
      card.querySelector('.scanner-result__image').alt = data.product.title;
      card.querySelector('.scanner-result__title').textContent = data.product.title;
      card.querySelector('.scanner-result__price').textContent = data.product.price;
      card.style.display = 'block';

      card.onclick = (e) => {
        e.preventDefault();
        this.close();
        window.location.href = productUrl;
      };
    } else {
      container.querySelector('.scanner-result__card').style.display = 'none';
    }

    const analysisEl = container.querySelector('.scanner-result__analysis');
    if (data.analysis) {
      analysisEl.textContent = data.analysis;
      analysisEl.style.display = 'block';
    } else {
      analysisEl.style.display = 'none';
    }

    const similarGrid = container.querySelector('.scanner-similar__grid');
    const similarSection = container.querySelector('.scanner-similar');
    const similar = data.similar || [];

    while (similarGrid.firstChild) similarGrid.removeChild(similarGrid.firstChild);

    if (similar.length > 0) {
      similar.forEach((p) => {
        const link = document.createElement('a');
        link.href = p.url;
        link.className = 'scanner-similar__item';
        link.onclick = (e) => { e.preventDefault(); this.close(); window.location.href = p.url; };

        const img = document.createElement('img');
        img.className = 'scanner-similar__img';
        img.src = p.image || '';
        img.alt = p.title;
        img.loading = 'lazy';
        link.appendChild(img);

        const name = document.createElement('div');
        name.className = 'scanner-similar__name';
        name.textContent = p.title;
        link.appendChild(name);

        similarGrid.appendChild(link);
      });
      similarSection.style.display = 'block';
    } else {
      similarSection.style.display = 'none';
    }

    const searchLink = container.querySelector('.scanner-search-link');
    if (data.searchQuery) {
      searchLink.href = `/search?q=${encodeURIComponent(data.searchQuery)}`;
      searchLink.style.display = 'block';
    } else {
      searchLink.style.display = data.matched ? 'none' : 'block';
      searchLink.href = '/search';
    }
    searchLink.onclick = (e) => { e.preventDefault(); this.close(); window.location.href = searchLink.href; };
  }

  showError(message) {
    this.setState('error');
    const errEl = this.modal.querySelector('.scanner-error__text');
    if (errEl) errEl.textContent = message;
  }

  retry() {
    this.scanning = false;
    this.unfreezeCamera();
    this.setState('idle');
    if (this.fileInput) this.fileInput.value = '';
    this.startCamera();
  }

  saveToHistory(data) {
    if (!data.matched || !data.product) return;
    try {
      const history = JSON.parse(localStorage.getItem('scannerHistory') || '[]');
      const entry = { ...data.product, scannedAt: new Date().toISOString() };
      const filtered = history.filter((h) => h.handle !== entry.handle);
      filtered.unshift(entry);
      localStorage.setItem('scannerHistory', JSON.stringify(filtered.slice(0, 10)));
    } catch { /* */ }
  }

  static renderHistory(containerId) {
    const container = document.getElementById(containerId);
    if (!container) return;
    while (container.firstChild) container.removeChild(container.firstChild);
    try {
      const history = JSON.parse(localStorage.getItem('scannerHistory') || '[]');
      if (history.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'scanner-page__history-empty';
        empty.textContent = '아직 스캔 기록이 없습니다.';
        container.appendChild(empty);
        return;
      }
      history.forEach((item) => {
        const link = document.createElement('a');
        link.href = item.url;
        link.className = 'scanner-page__history-item';
        const img = document.createElement('img');
        img.src = item.image || '';
        img.alt = item.title;
        link.appendChild(img);
        const span = document.createElement('span');
        span.textContent = item.title;
        link.appendChild(span);
        container.appendChild(link);
      });
    } catch { /* */ }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  // Bind scanner triggers to NutriLens (works with or without modal)
  document.querySelectorAll('[data-scanner-open]').forEach((btn) => {
    btn.addEventListener('click', (e) => { e.preventDefault(); window.location.href = '/pages/nutrilens#camera'; });
  });

  const modalEl = document.getElementById('scannerModal');
  if (!modalEl) return;
  const workerUrl = modalEl.dataset.workerUrl || '';
  const scanner = new ProductScanner({ workerUrl });
  scanner.init();
  window.productScanner = scanner;
  ProductScanner.renderHistory('scannerHistoryList');
});
